"use client";

import React from "react";
import { useParams } from "next/navigation";
import { formatPriceVND } from "@/utils/formatPrice";
import { Description } from "@/components/Common/Typography";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import { formatProductContent } from "@/utils/stripHTML";
import styles from "./productInfo.module.css";
import Image from "next/image";

interface ProductInfoProps {
  product: any;
  locale?: string;
}

export default function ProductInfo({ product, locale }: ProductInfoProps) {
  const params = useParams();
  const currentLocale = locale || (params?.locale as string) || "vi";
  const isEn = currentLocale === "en";

  const translated = useProductTranslation(
    {
      title: product?.ten_vt || product?.title || "",
      summary: product?.mieu_ta || product?.summary || "",
      packaging: product?.ten_dvt || "",
      category: product?.ten_nvt || "",
    },
    currentLocale
  );

  const displayTitle = translated.title || product?.ten_vt || product?.title;
  const displaySummary = translated.summary || product?.mieu_ta || product?.summary || "";
  const displayPackaging = translated.packaging || product?.ten_dvt || (isEn ? "Box" : "Thùng");
  const displayCategory = translated.category || product?.ten_nvt || (isEn ? "Updating..." : "Đang cập nhật");

  const price = product?.gia_ban_le;
  const numPrice = typeof price === "string" ? parseFloat(price) : Number(price || 0);
  const isContact = isNaN(numPrice) || numPrice <= 0;

  const originalPrice = product?.gia_ban_le0;
  const numOriginal = typeof originalPrice === "string" ? parseFloat(originalPrice) : Number(originalPrice || 0);
  const showOriginal = !isContact && numOriginal > numPrice;
  
  return (
    <div className={styles.info}>
      <h1 className={styles.title}>
        {displayTitle}
      </h1>
      
      <Description 
        as="div"
        size="medium"
        className={styles.shortDesc}
        dangerouslySetInnerHTML={{ 
          __html: formatProductContent(displaySummary) 
        }} 
      />

      <div className={styles.priceRow}>
        <span className={styles.label}>{isEn ? "Price:" : "Giá bán:"}</span>
        <span className={styles.price}>{formatPriceVND(price, currentLocale)}</span>
        {showOriginal && (
          <span className={styles.originalPrice}>{formatPriceVND(originalPrice, currentLocale)}</span>
        )}
      </div>

      <div className={styles.specs}>
        <div className={styles.specItem}>
          <Description as="span" size="small" className={styles.specLabel}>
            {isEn ? "Packaging:" : "Quy cách:"}
          </Description>
          <span className={styles.specValueBadge}>
            {displayPackaging}
          </span>
        </div>
        <div className={styles.specItem}>
          <Description as="span" size="small" className={styles.specLabel}>
            {isEn ? "Brand:" : "Thương hiệu:"}
          </Description>
          <span className={styles.specValueBadge}>
            {product?.ten_thuong_hieu || "Việt Thiên"}
          </span>
        </div>
        <div className={styles.specItem}>
          <Description as="span" size="small" className={styles.specLabel}>
            {isEn ? "Category:" : "Danh mục:"}
          </Description>
          <span className={styles.specValueBadge}>
            {displayCategory}
          </span>
        </div>
      </div>

      <div className={styles.ecomRow}>
        {product?.link_shopee && (
          <a href={product.link_shopee} target="_blank" rel="noopener noreferrer" className={`${styles.ecomBtn} ${styles.shopee}`} title="Shopee">
            <img src="/icons/shopee_logo.png" alt="Shopee" width={24} height={24} />
          </a>
        )}
        {product?.link_tiktok && (
          <a href={product.link_tiktok} target="_blank" rel="noopener noreferrer" className={`${styles.ecomBtn} ${styles.tiktok}`} title="TikTok">
            <img src="/icons/tiktok_logo.png" alt="TikTok" width={24} height={24} />
          </a>
        )}
        {product?.link_lazada && (
          <a href={product.link_lazada} target="_blank" rel="noopener noreferrer" className={`${styles.ecomBtn} ${styles.lazada}`} title="Lazada">
            <img src="/icons/lazada_logo.png" alt="Lazada" width={24} height={24} />
          </a>
        )}
        {product?.link_tiki && (
          <a href={product.link_tiki} target="_blank" rel="noopener noreferrer" className={`${styles.ecomBtn} ${styles.tiki}`} title="Tiki">
            <img src="/icons/tiki_logo.png" alt="Tiki" width={24} height={24} />
          </a>
        )}
      </div>
    </div>
  );
}
